import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { useCartStore } from '@/stores/cart';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { PRODUCT_CATEGORY_LABELS } from '@golf-booking/shared';

export function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore((state) => state.addItem);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api.products.get(productId!),
    enabled: !!productId,
  });

  const handleAddToCart = () => {
    if (!product?.data) return;

    addItem({
      productId: product.data.id,
      variantId: null,
      quantity,
      name: product.data.name,
      price: product.data.price,
      imageUrl: product.data.imageUrl,
    });

    toast.success(`Added ${quantity} ${product.data.name} to cart`);
  };

  if (isLoading) {
    return (
      <div className="container-wide py-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <Skeleton className="aspect-square rounded-2xl" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product?.data) {
    return (
      <div className="container-narrow py-16 text-center">
        <h1 className="text-2xl font-bold text-sand-900">Product not found</h1>
        <p className="mt-4 text-sand-500">This product may no longer be available.</p>
        <Link to="/shop" className="mt-8 inline-block">
          <Button>Back to Shop</Button>
        </Link>
      </div>
    );
  }

  const { data } = product;
  const inStock = data.quantity > 0;
  const lowStock = data.quantity > 0 && data.quantity <= 5;

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-wide">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-sand-500 hover:text-sand-700 mb-6">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to shop
        </Link>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Images */}
          <div>
            <div className="aspect-square rounded-2xl bg-white border border-sand-100 overflow-hidden">
              {data.imageUrl ? (
                <img
                  src={data.imageUrl}
                  alt={data.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-sand-100">
                  <svg className="h-24 w-24 text-sand-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {data.images && data.images.length > 0 && (
              <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar">
                {data.images.map((image: string, index: number) => (
                  <button
                    key={index}
                    className="flex-shrink-0 w-20 h-20 rounded-lg bg-white border border-sand-200 overflow-hidden hover:border-sage-500 transition-colors"
                  >
                    <img src={image} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-start gap-2 mb-2">
              <Badge variant="sand">
                {PRODUCT_CATEGORY_LABELS[data.category] || data.category}
              </Badge>
              {data.isRental && <Badge variant="terracotta">Rental</Badge>}
            </div>

            <h1 className="text-3xl font-bold text-sand-900">{data.name}</h1>

            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-3xl font-bold text-sage-600">
                {formatCurrency(data.price)}
              </span>
              {data.compareAtPrice && (
                <span className="text-xl text-sand-400 line-through">
                  {formatCurrency(data.compareAtPrice)}
                </span>
              )}
            </div>

            {/* Stock Status */}
            <div className="mt-4">
              {inStock ? (
                lowStock ? (
                  <p className="text-sm text-terracotta-600">Only {data.quantity} left in stock</p>
                ) : (
                  <p className="text-sm text-sage-600">In stock</p>
                )
              ) : (
                <p className="text-sm text-sand-400">Out of stock</p>
              )}
            </div>

            {/* Description */}
            {data.description && (
              <div className="mt-6">
                <h2 className="text-sm font-semibold text-sand-900 mb-2">Description</h2>
                <p className="text-sand-600">{data.description}</p>
              </div>
            )}

            {/* Rental Info */}
            {data.isRental && data.rentalDuration && (
              <Card className="mt-6 bg-sand-50 border-sand-200">
                <div className="flex items-center gap-3">
                  <svg className="h-6 w-6 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-sand-900">Rental Period</p>
                    <p className="text-sm text-sand-500">{data.rentalDuration} day{data.rentalDuration > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Add to Cart */}
            <div className="mt-8">
              <div className="flex items-center gap-4 mb-4">
                <label className="text-sm font-medium text-sand-700">Quantity</label>
                <div className="flex items-center border border-sand-200 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 text-sand-600 hover:text-sand-900"
                    disabled={quantity <= 1}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="px-4 py-2 text-sand-900 font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(data.quantity, quantity + 1))}
                    className="px-3 py-2 text-sand-600 hover:text-sand-900"
                    disabled={quantity >= data.quantity}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>

              <Button
                onClick={handleAddToCart}
                size="lg"
                variant="sage"
                className="w-full"
                disabled={!inStock}
              >
                {inStock ? 'Add to Cart' : 'Out of Stock'}
              </Button>
            </div>

            {/* SKU */}
            <p className="mt-6 text-xs text-sand-400">SKU: {data.sku}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
